import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  FileText,
  FileClock,
  AlertTriangle,
  Database,
  LucideIcon,
  Activity,
  Cpu,
  Wrench,
  BarChart2,
  Mail,
  BookOpen,
  ShieldAlert,
  Layers,
  Tag,
  Globe,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useArticles, useSources } from '@/hooks/queries'
import type { Article } from '@/lib/types'

// ─── Stat card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string
  value: string | number
  icon: LucideIcon
  isLoading?: boolean
  subtitle?: string
}

function StatCard({ label, value, icon: Icon, isLoading, subtitle }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <div className="flex flex-col gap-1">
            <div className="text-3xl font-bold">{value}</div>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Admin stats hook ─────────────────────────────────────────────────────────

function useAdminStats() {
  const { data: articles, isLoading } = useArticles({ limit: 200 })

  const stats = useMemo(() => {
    if (!articles) return { published: 0, drafts: 0, critical: 0 }

    const published = articles.filter((a) => a.status === 'published').length
    const drafts = articles.filter((a) => a.status === 'draft').length
    const critical = articles.filter((a) => a.riskScore >= 80).length

    return { published, drafts, critical }
  }, [articles])

  return { stats, articles, isLoading }
}

// ─── Risk score badge ─────────────────────────────────────────────────────────

function RiskBadge({ score }: { score: number }) {
  const variant =
    score >= 80
      ? 'destructive'
      : score >= 60
        ? 'secondary'
        : 'outline'

  return (
    <Badge variant={variant} className="text-xs tabular-nums">
      {score}
    </Badge>
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: Article['status'] }) {
  const cls =
    status === 'published'
      ? 'bg-green-500/15 text-green-400 border-green-500/30'
      : status === 'draft'
        ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
        : status === 'suppressed'
          ? 'bg-red-500/15 text-red-400 border-red-500/30'
          : 'bg-muted text-muted-foreground'

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  )
}

// ─── Recent articles table ────────────────────────────────────────────────────

function RecentArticles({
  articles,
  isLoading,
}: {
  articles: Article[] | undefined
  isLoading: boolean
}) {
  const recent = useMemo(() => {
    if (!articles) return []
    return [...articles]
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
      .slice(0, 5)
  }, [articles])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent Articles</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex flex-col gap-3 p-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6">No articles found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="px-6 py-3 text-left font-medium">Title</th>
                  <th className="px-4 py-3 text-left font-medium hidden sm:table-cell">Category</th>
                  <th className="px-4 py-3 text-center font-medium">Risk</th>
                  <th className="px-4 py-3 text-center font-medium hidden md:table-cell">Status</th>
                  <th className="px-4 py-3 text-right font-medium hidden lg:table-cell">Published</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((article) => (
                  <tr key={article.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3">
                      <span className="line-clamp-1 font-medium">{article.title}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-muted-foreground capitalize">
                        {article.category?.replace(/_/g, ' ') ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <RiskBadge score={article.riskScore} />
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell">
                      <StatusBadge status={article.status} />
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground hidden lg:table-cell">
                      {new Date(article.publishedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Admin navigation sections ────────────────────────────────────────────────

const ADMIN_NAV_SECTIONS = [
  {
    href: '/admin/observability',
    label: 'Observability',
    description: 'Service health, Sentry errors, Grafana logs, and DB pool stats.',
    icon: Activity,
  },
  {
    href: '/admin/ai/providers',
    label: 'AI Providers',
    description: 'Configure and manage AI provider API keys and settings.',
    icon: Cpu,
  },
  {
    href: '/admin/ai/tasks',
    label: 'AI Tasks',
    description: 'Map pipeline tasks to providers, models, and prompt templates.',
    icon: Wrench,
  },
  {
    href: '/admin/frameworks',
    label: 'Frameworks',
    description: 'Manage regulatory and compliance framework definitions.',
    icon: BookOpen,
  },
  {
    href: '/admin/benchmarks',
    label: 'Benchmarks',
    description: 'Review, approve, and manage benchmark entries.',
    icon: BarChart2,
  },
  {
    href: '/admin/vulnerabilities',
    label: 'Vulnerabilities',
    description: 'Review and override vulnerability data and EPSS scores.',
    icon: ShieldAlert,
  },
  {
    href: '/admin/db/perf',
    label: 'DB Perf',
    description: 'Slow queries, table scan ratios, and pg_stat_statements top queries.',
    icon: Database,
  },
  {
    href: '/admin/sources',
    label: 'Sources',
    description: 'Enable or disable content ingestion sources.',
    icon: Database,
  },
  {
    href: '/admin/email',
    label: 'Email Config',
    description: 'Configure email delivery settings and templates.',
    icon: Mail,
  },
  {
    href: '/admin/stories',
    label: 'Story Management',
    description: 'Review, moderate, and manage story clusters.',
    icon: Layers,
  },
  {
    href: '/admin/cpe-mappings',
    label: 'CPE Vendor Mappings',
    description:
      'Triage unmapped CPE vendor tokens and resolve seeder conflicts.',
    icon: Tag,
  },
  {
    href: '/admin/ransomware-groups',
    label: 'Ransomware Groups Triage',
    description:
      'Map leak-site ransomware groups to threat actors. Manual disambiguation after auto-mapper plateau.',
    icon: Tag,
  },
  {
    href: '/admin/country-overrides',
    label: 'Country Overrides',
    description:
      'Persistent entity-level country overrides that survive scraper UPSERTs (audit-14 fix).',
    icon: Globe,
  },
] as const

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminDashboardPage() {
  const { stats, articles, isLoading } = useAdminStats()
  const { data: sources, isLoading: sourcesLoading } = useSources()
  const activeSources = sources?.filter((s) => s.enabled).length ?? 0
  const totalSources = sources?.length ?? 0

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Platform health, content stats, and recent activity.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Published Articles"
          value={stats.published}
          icon={FileText}
          isLoading={isLoading}
        />
        <StatCard
          label="Draft Articles"
          value={stats.drafts}
          icon={FileClock}
          isLoading={isLoading}
        />
        <StatCard
          label="Critical (Risk ≥ 80)"
          value={stats.critical}
          icon={AlertTriangle}
          isLoading={isLoading}
        />
        <StatCard
          label="Active Sources"
          value={activeSources}
          icon={Database}
          isLoading={sourcesLoading}
          subtitle={
            totalSources > 0
              ? `of ${totalSources} configured`
              : undefined
          }
        />
      </div>

      {/* Recent activity */}
      <RecentArticles articles={articles} isLoading={isLoading} />

      {/* Admin section navigation */}
      <div>
        <h2 className="text-base font-semibold tracking-tight mb-3">Admin sections</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {ADMIN_NAV_SECTIONS.map((section) => (
            <Link key={section.href} to={section.href}>
              <Card className="h-full hover:bg-muted/40 transition-colors cursor-pointer">
                <CardHeader className="flex flex-row items-center gap-3 pb-1 pt-4 px-4">
                  <section.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <CardTitle className="text-sm font-medium">{section.label}</CardTitle>
                </CardHeader>
                <CardContent className="pb-4 px-4">
                  <p className="text-xs text-muted-foreground">{section.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
